import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import api from '../utils/axios';
import { API_ENDPOINTS } from '../config/api';

const FieldManager = () => {
  const [titleField, setTitleField] = useState(null);
  const [showAddOption, setShowAddOption] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newOption, setNewOption] = useState({ value: '', label: '' });

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Number' },
    { value: 'number', label: 'Number' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'select', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'radio', label: 'Radio Buttons' },
    { value: 'date', label: 'Date' }
  ];

  // Fetch or create title field
  const fetchTitleField = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_ENDPOINTS.FORM_FIELDS);
      const data = response.data;
      
      if (data.success) {
        // Look for title field (check both 'title' and any field with title-like names)
        let titleField = data.fields.find(field => 
          field.name === 'title' || 
          field.name === 'lead_title' || 
          field.label?.toLowerCase().includes('title') ||
          field.label?.toLowerCase().includes('purpose')
        );
        
        if (titleField) {
          // If found but name is not 'title', update it
          if (titleField.name !== 'title') {
            const updateResponse = await api.put(API_ENDPOINTS.FORM_FIELD_BY_ID(titleField._id), {
              ...titleField,
              name: 'title',
              label: '🎯 Lead Title / Purpose',
              type: 'select'
            });
            if (updateResponse.data.success) {
              titleField = updateResponse.data.field;
            }
          }
        } else {
          // Create new title field only if none exists
          const createResponse = await api.post(API_ENDPOINTS.FORM_FIELDS, {
            name: 'title',
            label: '🎯 Lead Title / Purpose',
            type: 'select',
            placeholder: 'Select your enquiry purpose...',
            required: true,
            options: [
              { value: 'website_development', label: 'Website Development' },
              { value: 'mobile_app_development', label: 'Mobile App Development' },
              { value: 'digital_marketing', label: 'Digital Marketing' },
              { value: 'ecommerce_solution', label: 'E-commerce Solution' },
              { value: 'software_development', label: 'Software Development' }
            ],
            formType: 'both'
          });
          if (createResponse.data.success) {
            titleField = createResponse.data.field;
          } else {
            console.error('Failed to create title field:', createResponse.data);
          }
        }
        
        setTitleField(titleField);
      } else {
        console.error('Failed to fetch form fields:', data);
      }
    } catch (error) {
      console.error('Error fetching title field:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTitleField();
  }, []);

  const handleAddOption = async () => {
    if (!newOption.value || !newOption.label) {
      alert('Please enter both value and label for the option');
      return;
    }

    // Wait for field to load if it's still loading
    if (loading) {
      alert('Loading title field... Please wait a moment.');
      return;
    }

    if (!titleField) {
      // Try to fetch the field again
      await fetchTitleField();
      if (!titleField) {
        alert('Unable to load title field. Please refresh the page and try again.');
        return;
      }
    }

    try {
      const updatedOptions = [...(titleField.options || []), newOption];
      const response = await api.put(API_ENDPOINTS.FORM_FIELD_BY_ID(titleField._id), { 
        ...titleField, 
        options: updatedOptions 
      });
      
      if (response.data.success) {
        setTitleField({ ...titleField, options: updatedOptions });
        setNewOption({ value: '', label: '' });
        setShowAddOption(false);
      } else {
        alert(response.data.message || 'Error adding option');
      }
    } catch (error) {
      console.error('Error adding option:', error);
      alert('Error adding option');
    }
  };

  const handleDeleteOption = async (optionIndex) => {
    if (window.confirm('Are you sure you want to delete this option?')) {
      try {
        const updatedOptions = titleField.options.filter((_, index) => index !== optionIndex);
        const response = await api.put(API_ENDPOINTS.FORM_FIELD_BY_ID(titleField._id), { 
          ...titleField, 
          options: updatedOptions 
        });
        
        if (response.data.success) {
          setTitleField({ ...titleField, options: updatedOptions });
        } else {
          alert(response.data.message || 'Error deleting option');
        }
      } catch (error) {
        console.error('Error deleting option:', error);
        alert('Error deleting option');
      }
    }
  };


  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1">🎯 Title Field Options Manager</h2>
          <p className="text-gray-600 text-sm">Manage dropdown options for the Lead Title/Purpose field</p>
        </div>
        <button
          onClick={() => setShowAddOption(true)}
          disabled={loading || !titleField}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
            loading || !titleField 
              ? 'bg-gray-400 text-white cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {loading ? 'Loading...' : 'Add Option'}
        </button>
      </div>

      {/* Add Option Form */}
      {showAddOption && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-medium text-gray-900 mb-4">Add New Option to Title Field</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Option Value</label>
              <input
                type="text"
                value={newOption.value}
                onChange={(e) => setNewOption({...newOption, value: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., web_design, seo_services"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Option Label</label>
              <input
                type="text"
                value={newOption.label}
                onChange={(e) => setNewOption({...newOption, label: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Web Design, SEO Services"
              />
            </div>
          </div>

          <div className="mt-4 flex space-x-3">
            <button
              onClick={handleAddOption}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Add Option
            </button>
            <button
              onClick={() => {
                setShowAddOption(false);
                setNewOption({ value: '', label: '' });
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors flex items-center"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Title Field Options List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading title field...</div>
          </div>
        ) : !titleField ? (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-2">Title field not found</div>
            <p className="text-sm text-gray-400">Creating default title field...</p>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="mb-4">
              <h3 className="font-medium text-gray-900 mb-2">{titleField.label}</h3>
              <p className="text-sm text-gray-600">
                <strong>Field Name:</strong> {titleField.name} | 
                <strong>Type:</strong> {titleField.type} | 
                <strong>Required:</strong> {titleField.required ? 'Yes' : 'No'}
              </p>
            </div>
            
            <div className="mt-4">
              <h4 className="font-medium text-gray-800 mb-3">Current Options ({titleField.options?.length || 0}):</h4>
              {titleField.options && titleField.options.length > 0 ? (
                <div className="space-y-2">
                  {titleField.options.map((option, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded border border-gray-200">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{option.label}</div>
                        <div className="text-sm text-gray-500">Value: {option.value}</div>
                      </div>
                      <button
                        onClick={() => handleDeleteOption(index)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete option"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No options added yet. Click "Add Option" to create your first option.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-2">💡 How Title Field Management Works:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Fixed Field Name:</strong> The field name is always "title" - cannot be changed</li>
          <li>• <strong>Dynamic Options:</strong> Add/remove dropdown options for different services</li>
          <li>• <strong>Add Options:</strong> Click "Add Option" to create new service options</li>
          <li>• <strong>Delete Options:</strong> Click 🗑️ to remove unwanted options</li>
          <li>• <strong>Auto-Sync:</strong> Changes automatically appear in all lead capture forms</li>
        </ul>
        
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
          <h4 className="font-medium text-green-800 mb-1">✅ Current Setup:</h4>
          <p className="text-xs text-green-700">
            Field Name: <code className="bg-green-100 px-1 rounded">title</code> | 
            Type: <strong>Dropdown (Select)</strong> | 
            Shows in: <strong>All Forms</strong> | 
            Required: <strong>Yes</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

// Edit Field Form Component
const EditFieldForm = ({ field, fieldTypes, onSave, onCancel }) => {
  const [editData, setEditData] = useState({
    name: field.name,
    label: field.label,
    type: field.type,
    placeholder: field.placeholder || '',
    required: field.required || false,
    options: field.options || [],
    formType: field.formType || 'both'
  });

  const addOption = () => {
    setEditData({
      ...editData,
      options: [...editData.options, { value: '', label: '' }]
    });
  };

  const updateOption = (index, key, value) => {
    const updatedOptions = [...editData.options];
    updatedOptions[index][key] = value;
    setEditData({ ...editData, options: updatedOptions });
  };

  const removeOption = (index) => {
    setEditData({
      ...editData,
      options: editData.options.filter((_, i) => i !== index)
    });
  };

  const handleSave = () => {
    onSave(editData);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Field Name *
          </label>
          <input
            type="text"
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Field Label *
          </label>
          <input
            type="text"
            value={editData.label}
            onChange={(e) => setEditData({ ...editData, label: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Field Type *
          </label>
          <select
            value={editData.type}
            onChange={(e) => setEditData({ ...editData, type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {fieldTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Placeholder Text
          </label>
          <input
            type="text"
            value={editData.placeholder}
            onChange={(e) => setEditData({ ...editData, placeholder: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Options for select/radio/checkbox */}
      {['select', 'radio', 'checkbox'].includes(editData.type) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Options
          </label>
          {editData.options.map((option, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <input
                type="text"
                placeholder="Value"
                value={option.value}
                onChange={(e) => updateOption(index, 'value', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Label"
                value={option.label}
                onChange={(e) => updateOption(index, 'label', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={() => removeOption(index)}
                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            onClick={addOption}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            + Add Option
          </button>
        </div>
      )}

      <div className="flex items-center gap-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={editData.required}
            onChange={(e) => setEditData({ ...editData, required: e.target.checked })}
            className="mr-2"
          />
          Required Field
        </label>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Show in Forms
          </label>
          <select
            value={editData.formType}
            onChange={(e) => setEditData({ ...editData, formType: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="both">Both Forms</option>
            <option value="lead">Lead Form Only</option>
          </select>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={handleSave}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          <CheckIcon className="h-4 w-4" />
          Save Changes
        </button>
        <button
          onClick={onCancel}
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 flex items-center gap-2"
        >
          <XMarkIcon className="h-4 w-4" />
          Cancel
        </button>
      </div>
    </div>
  );
};

export default FieldManager;
